<template>
  <div>
    <div class="mb-2 d-flex align-items-center gap-2">
      <router-link
        :to="{
          name: 'sale-prices-rules-selection'
        }"
        class="d-flex align-items-center gap-2"
      >
        <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
        {{ $t('Back to Provider and Customer Selection') }}
      </router-link>
    </div>

    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="rulesForm">
        <b-form ref="rulesFormElement" class="" @submit.prevent="submit">
          <b-row>
            <b-col cols="12">
              <div class="rules-list">
                <template v-if="loading">
                  <div v-for="i in 3" :key="i" class="service-item">
                    <b-skeleton width="80%" height="24px" class="mb-0" />
                    <b-skeleton width="80%" height="24px" class="mb-0" />
                    <b-skeleton width="80%" height="24px" class="mb-0" />
                    <b-skeleton width="80%" height="24px" class="mb-0" />
                    <b-skeleton width="80%" height="24px" class="mb-0" />
                    <b-skeleton width="80%" height="24px" class="mb-0" />
                    <b-skeleton width="80%" height="24px" class="mb-0" />
                    <b-skeleton width="80%" height="24px" class="mb-0" />
                    <b-skeleton width="80%" height="24px" class="mb-0" />
                    <b-skeleton width="80%" height="24px" class="mb-0" />
                  </div>
                </template>
                <template v-else-if="rules.length > 0">
                  <div class="service-item-header">
                    <div class="service-name">
                      <label>{{ t('Provider') }}</label>
                    </div>
                    <div class="service-type">
                      <label>{{ t('Rule Type') }}</label>
                    </div>
                    <div class="service-operator">
                      <label>{{ t('Operator') }}</label>
                    </div>
                    <div class="service-countries">
                      <label>{{ t('Countries') }}</label>
                    </div>
                    <div class="service-value">
                      <label>{{ t('Value') }}</label>
                    </div>
                    <div class="service-price-type">
                      <label>{{ t('Price Type') }}</label>
                    </div>
                    <div class="service-price">
                      <label>{{ t('Cost Price') }}</label>
                    </div>
                    <div class="service-min-price">
                      <label>{{ t('Cost Min Price') }}</label>
                    </div>
                    <div class="service-sale-price">
                      <label>{{ t('Sale Price') }}</label>
                    </div>
                    <div class="service-sale-min-price">
                      <label>{{ t('Sale Min Price') }}</label>
                    </div>
                  </div>
                  <div v-for="rule in rules" :key="rule.provider_rule_id" class="service-item">
                    <!-- Provider Name -->
                    <div class="service-name">
                      <label>{{
                        rule.provider ? rule.provider.name : rule.providerName || '-'
                      }}</label>
                    </div>

                    <!-- Type -->
                    <div class="service-type">
                      <label>{{ rule.type }}</label>
                    </div>

                    <!-- Operator -->
                    <div class="service-operator">
                      <label>{{ rule.operator }}</label>
                    </div>

                    <!-- Countries -->
                    <div class="service-countries">
                      <template v-if="rule.countries && rule.countries.length">
                        <span v-if="rule.countries.length <= 2">
                          <b-badge
                            v-for="country in rule.countries"
                            :key="country.id || country.iso2"
                            variant="light-primary"
                            class="mr-50 mb-50"
                          >
                            {{ getCountryLabel(country) }}
                          </b-badge>
                        </span>
                        <span v-else>
                          <b-badge
                            v-for="country in rule.countries.slice(0, 2)"
                            :key="country.id || country.iso2"
                            variant="light-primary"
                            class="mr-50 mb-50"
                          >
                            {{ getCountryLabel(country) }}
                          </b-badge>
                          <b-badge
                            :id="`rule-country-tooltip-${rule.provider_rule_id}`"
                            variant="light-secondary"
                            class="cursor-pointer"
                          >
                            +{{ rule.countries.length - 2 }}
                          </b-badge>
                          <b-tooltip
                            :target="`rule-country-tooltip-${rule.provider_rule_id}`"
                            triggers="hover"
                            placement="top"
                          >
                            {{ rule.countries.map(c => getCountryLabel(c)).join(', ') }}
                          </b-tooltip>
                        </span>
                      </template>
                      <label v-else>-</label>
                    </div>

                    <!-- Value -->
                    <div class="service-value">
                      <label>{{
                        Number(rule.rawType) === 6
                          ? '-'
                          : rule.value != null && rule.value !== ''
                          ? rule.value
                          : '-'
                      }}</label>
                    </div>

                    <!-- Price Type -->
                    <div class="service-price-type">
                      <label>{{ rule.pricingType }}</label>
                    </div>

                    <!-- Cost Price (Disabled) -->
                    <div class="service-price">
                      <b-form-input type="number" :value="rule.price" placeholder="0.00" disabled />
                    </div>

                    <!-- Cost Min Price (Disabled) -->
                    <div class="service-min-price">
                      <b-form-input
                        type="number"
                        :value="
                          rule.minimumPrice != null && rule.minimumPrice !== ''
                            ? rule.minimumPrice
                            : ''
                        "
                        placeholder="-"
                        disabled
                      />
                    </div>

                    <!-- Sale Price Input -->
                    <div class="service-sale-price">
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('Sale Price')"
                        rules="required|doubleOrInteger|positive"
                      >
                        <b-form-input
                          type="number"
                          v-model="rule.salePrice"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </div>

                    <!-- Sale Min Price Input -->
                    <div class="service-sale-min-price">
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('Sale Minimum Price')"
                        rules="doubleOrInteger|positive"
                      >
                        <b-form-input
                          type="number"
                          v-model="rule.saleMinimumPrice"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </div>

                    <b-alert
                      v-if="
                        rule.salePrice && rule.price && Number(rule.salePrice) < Number(rule.price)
                      "
                      variant="warning"
                      show
                      class="mt-1 p-1 w-100"
                      style="grid-column: 1 / -1"
                    >
                      <strong>
                        <feather-icon icon="AlertTriangleIcon" size="14" class="mr-1" />
                        <small class="mr-1 font-weight-bolder">
                          {{ t('Sale price less than cost price is not recommended') }}
                        </small>
                      </strong>
                    </b-alert>

                    <b-alert
                      v-if="
                        rule.saleMinimumPrice &&
                        rule.minimumPrice &&
                        Number(rule.saleMinimumPrice) < Number(rule.minimumPrice)
                      "
                      variant="warning"
                      show
                      class="mt-1 p-1 w-100"
                      style="grid-column: 1 / -1"
                    >
                      <strong>
                        <feather-icon icon="AlertTriangleIcon" size="14" class="mr-1" />
                        <small class="mr-1 font-weight-bolder">
                          {{
                            t('Sale minimum price less than cost minimum price is not recommended')
                          }}
                        </small>
                      </strong>
                    </b-alert>
                  </div>
                </template>
                <template v-else>
                  <div class="empty-state text-center p-4">
                    <feather-icon icon="PackageIcon" size="48" class="mb-2" />
                    <h4>{{ t('No Rules') }}</h4>
                    <p class="text-muted">
                      {{ t('There are no rules available for this provider.') }}
                    </p>
                    <b-button
                      variant="outline-primary"
                      class="mt-2"
                      @click="$router.push({ name: 'sale-prices-rules-selection' })"
                    >
                      <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
                      {{ t('Back to Selection') }}
                    </b-button>
                  </div>
                </template>
              </div>
            </b-col>

            <!-- Submit Button -->
            <b-col cols="12">
              <div class="d-flex gap-3 align-items-center justify-content-end mt-2">
                <b-button
                  variant="primary"
                  class="d-flex align-items-center"
                  type="submit"
                  :disabled="rules.length === 0"
                >
                  {{ submitting ? t('Please Wait') : t('Save') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCol,
  BForm,
  BRow,
  BButton,
  BOverlay,
  BFormInput,
  BSkeleton,
  BAlert,
  BBadge,
  BTooltip
} from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';

export default {
  name: 'SalePriceRulesForm',
  components: {
    BOverlay,
    BButton,
    BRow,
    BCol,
    BForm,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    BSkeleton,
    BAlert,
    BBadge,
    BTooltip
  },

  mixins: [helper],

  data() {
    return {
      rules: [],
      submitting: false,
      loading: false,
      ruleTypeOptions: [],
      operatorOptions: [],
      priceTypeOptions: []
    };
  },

  async created() {
    this.$store.dispatch('app/getCountries');
    await this.fetchRules();
  },

  methods: {
    getCountryLabel(country) {
      if (!country) return '';
      const isTr = this.$i18n.locale === 'tr';
      if (typeof country === 'object') {
        const name = (isTr && country.nameTr ? country.nameTr : country.name) || country.iso2;
        if (name) return name;
      }
      const countryId = typeof country === 'object' ? country.id : country;
      const storeCountries = this.$store?.getters['app/countries'] || [];
      const found = storeCountries.find(c => Number(c.value || c.id) === Number(countryId));
      if (found) {
        return (
          (isTr && found.nameTr ? found.nameTr : found.text || found.name) ||
          found.iso2 ||
          countryId
        );
      }
      return countryId;
    },

    async fetchRules() {
      try {
        this.loading = true;
        const params = {
          providerId: this.$route.params.providerId,
          customerId: this.$route.params.customerId
        };

        const [typeOptionsResponse, operatorOptionsResponse, pricingTypeOptionsResponse] =
          await Promise.all([
            useService.constantService.getProviderRuleType(),
            useService.constantService.getProviderRuleOperator(),
            useService.constantService.getProviderRulePricingType()
          ]);

        this.ruleTypeOptions = typeOptionsResponse.data;
        this.operatorOptions = operatorOptionsResponse.data;
        this.priceTypeOptions = pricingTypeOptionsResponse.data;

        const response = await useService.pricingService.getSalePricesRules(params);

        this.rules = response.data.map(rule => ({
          ...rule,
          rawType: rule.type,
          type: this.ruleTypeOptions.find(type => type.value === rule.type)?.info || rule.type,
          operator:
            this.operatorOptions.find(operator => operator.value === rule.operator)?.info ||
            rule.operator,
          pricingType:
            this.priceTypeOptions.find(pricingType => pricingType.value === rule.pricingType)
              ?.info || rule.pricingType,
          salePrice: rule.salePrice != null ? rule.salePrice : '',
          saleMinimumPrice: rule.saleMinimumPrice != null ? rule.saleMinimumPrice : ''
        }));
      } catch (error) {
        // console.error('Error fetching rules:', error);
      } finally {
        this.loading = false;
      }
    },

    submit() {
      this.$refs.rulesForm.validate().then(success => {
        if (success) {
          const formData = {
            prices: this.rules.map(rule => {
              const item = {
                provider_rule_id: rule.provider_rule_id,
                salePrice: Number(rule.salePrice || 0)
              };

              if (
                rule.saleMinimumPrice !== '' &&
                rule.saleMinimumPrice !== null &&
                rule.saleMinimumPrice !== undefined &&
                !Number.isNaN(Number(rule.saleMinimumPrice))
              ) {
                item.saleMinimumPrice = Number(rule.saleMinimumPrice);
              }

              return item;
            })
          };

          this.submitting = true;

          useService.pricingService
            .saveSalePricesRules({
              customerId: this.$route.params.customerId,
              providerId: this.$route.params.providerId,
              ...formData
            })
            .then(() => {
              this.showSuccessMessage(this.$t('Rules saved successfully'));
              this.$router.push({ name: 'sale-prices-rules' });
            })
            .catch(error => {
              let msg = error?.response?.data?.message || error?.data?.message || error?.message;
              if (!msg && Array.isArray(error)) {
                msg = error.map(e => e.message || e).join('; ');
              } else if (!msg && typeof error === 'object') {
                const errObj = error.errors || error;
                if (typeof errObj === 'object') {
                  msg = Object.values(errObj).flat().join('; ');
                }
              }
              this.showErrorMessage(msg || this.$t('An error occurred'));
            })
            .finally(() => {
              this.submitting = false;
            });
        }
      });
    }
  },

  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style scoped lang="scss">
.rules-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
}

.service-item {
  display: grid;
  grid-template-columns: 1.1fr 1fr 1fr 1.4fr 0.8fr 1.1fr 0.9fr 0.9fr 1fr 1fr;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-width: 1250px;
}

body.dark-layout {
  .service-item {
    background-color: #2d3748;
  }
}

.service-item-header {
  display: grid;
  grid-template-columns: 1.1fr 1fr 1fr 1.4fr 0.8fr 1.1fr 0.9fr 0.9fr 1fr 1fr;
  gap: 1rem;
  border-radius: 4px;
  padding: 0 1rem;
  font-weight: 600;
  min-width: 1250px;
}

.service-name {
  display: flex;
  align-items: center;
}

.service-name label {
  margin: 0;
  font-weight: 500;
}

.service-price-type,
.service-price-value {
  min-width: 120px;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
